import type React from "react";
import { MicroLabel } from "./MicroLabel";
import { cn } from "@/lib/utils";
import { wrappedIndex } from "@/lib/wrapped-index";

/** The small control each choice is drawn as, and the fill the chosen one takes. */
export const CHOICE_CONTROL =
  "flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-card px-2.5 py-[5px] text-[11px] font-semibold";
export const CHOICE_CHOSEN =
  "border-primary bg-primary text-primary-foreground";

/**
 * Which way an arrow key steps, or nothing when the key is not one.
 *
 * Both axes, because a radiogroup holds no caret for Left and Right to move through. A
 * combobox over a text field answers the vertical pair alone for that same reason, which is
 * why the reading of the key stays with the group and only the arithmetic is shared.
 */
function arrowStep(key: string): -1 | 1 | undefined {
  if (key === "ArrowRight" || key === "ArrowDown") return 1;
  if (key === "ArrowLeft" || key === "ArrowUp") return -1;
  return undefined;
}

/**
 * Move focus to another choice in the same group, by how far along it is.
 *
 * Read off the group rather than held in a ref per control, because the group is the thing
 * that owns the arrangement and a ref array would have to be kept in step with a list whose
 * length changes when the shape does.
 */
function focusSibling(from: HTMLElement, by: number): void {
  const group = from.parentElement;
  if (group === null) return;
  const choices = [...group.children];
  const to = choices[choices.indexOf(from) + by];
  if (to instanceof HTMLElement) to.focus();
}

/**
 * Answer an arrow key on one radio of a group: focus the radio it steps to, and say which.
 *
 * The index stepped to, for the caller to choose, or nothing for any other key. For a
 * radiogroup drawn its own way (pictures rather than words) that keeps the group's keys.
 */
export function stepRadio(
  event: React.KeyboardEvent<HTMLElement>,
  index: number,
  count: number,
): number | undefined {
  const step = arrowStep(event.key);
  if (step === undefined) return undefined;
  event.preventDefault();
  const to = wrappedIndex(index, step, count);
  // Focus moves with the choice. Left behind it sits on a control the group is about to
  // take out of the tab order, and the next key steps from there.
  focusSibling(event.currentTarget, to - index);
  return to;
}

export interface Choice<T> {
  /** What the control shows. */
  label: string;
  value: T;
  /** What a reader hears, where the label is a picture of the choice rather than its name. */
  name?: string;
}

/**
 * One setting, offered as a row (or a stack) of small buttons, one answer between them.
 *
 * A radiogroup rather than a row of toggles: the choices are one answer between them, and a
 * reader told they are separate toggles has no way to know that pressing one un-presses the
 * others. Only the current choice is in the tab order and the arrow keys move within the
 * group, choosing as they go, which is what a radiogroup promises a reader.
 */
export function ChoiceGroup<T extends string | number | boolean>({
  label,
  choices,
  chosen,
  onChoose,
  stacked = false,
  disabled = false,
}: {
  label: string;
  choices: readonly Choice<T>[];
  chosen: T;
  onChoose: (value: T) => void;
  stacked?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="mt-3 first:mt-0">
      <MicroLabel>{label}</MicroLabel>
      <div
        role="radiogroup"
        aria-label={label}
        className={cn("mt-1.5 flex gap-1.5", stacked && "flex-col")}
      >
        {choices.map((choice, index) => (
          <button
            key={String(choice.value)}
            type="button"
            role="radio"
            aria-checked={choice.value === chosen}
            aria-label={choice.name}
            disabled={disabled}
            tabIndex={choice.value === chosen ? 0 : -1}
            onKeyDown={(event) => {
              const to = stepRadio(event, index, choices.length);
              if (to !== undefined) onChoose(choices[to].value);
            }}
            onClick={() => {
              onChoose(choice.value);
            }}
            className={cn(
              CHOICE_CONTROL,
              "disabled:opacity-50",
              stacked && "w-full justify-center",
              choice.value === chosen && CHOICE_CHOSEN,
            )}
          >
            {choice.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * One answer to a question, drawn as v0.28 draws its answers: a box with a
 * round mark for one answer among several, a square one where several can be
 * chosen, the chosen one edged in the brand on its wash. Named by its words
 * alone; the mark is drawing.
 */
export function ChoiceRow({
  role,
  checked,
  onChoose,
  children,
}: {
  role: "radio" | "checkbox";
  checked: boolean;
  onChoose: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={checked}
      onClick={onChoose}
      className="group flex cursor-pointer items-center gap-2 rounded-tip border border-border bg-card px-3 py-2 text-left text-[13px] font-medium text-foreground hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 aria-checked:border-primary aria-checked:bg-muted aria-checked:font-bold"
    >
      <span
        aria-hidden
        data-choice-mark
        className={cn(
          "size-[14px] flex-none border-[1.5px] border-subtle-foreground bg-card group-aria-checked:border-[4.5px] group-aria-checked:border-primary",
          role === "radio" ? "rounded-full" : "rounded-[4px]",
        )}
      />
      {children}
    </button>
  );
}
